import React from 'react';
import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PAGE_HEADER_HEIGHT_PX,
  pageTitleSx,
} from '../../constants/pageLayout';

const PageLayout = ({
  title,
  children,
  contentSx,
  sx,
  topBar,
  titleBarContent,
  afterBackContent,
  allowTitleBarWrap = false,
  backTo,
  backLabel = 'Back',
  ...props
}) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={[
        {
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'auto',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...props}
    >
      {title && (
        <Paper
          elevation={0}
          className="fade-in"
          sx={{
            ...(allowTitleBarWrap
              ? {
                  minHeight: PAGE_HEADER_HEIGHT_PX,
                }
              : {
                  height: PAGE_HEADER_HEIGHT_PX,
                  minHeight: PAGE_HEADER_HEIGHT_PX,
                }),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            borderRadius: 0,
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            boxSizing: 'border-box',
          }}
        >
          <Box
            className="staggered-animation"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
          >
            {backTo && (
              <Tooltip title={backLabel}>
                <IconButton
                  size="small"
                  onClick={() => navigate(backTo)}
                  sx={{ color: 'text.secondary', mr: 1, flexShrink: 0 }}
                  aria-label={backLabel}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {afterBackContent}
            <Typography
              variant="h6"
              component="h1"
              sx={{ ...pageTitleSx, flexShrink: 0 }}
            >
              {title}
            </Typography>
            {titleBarContent && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: allowTitleBarWrap ? 'flex-start' : 'center',
                  gap: 2,
                  flex: 1,
                  pl: 2,
                  ...(allowTitleBarWrap && {
                    flexWrap: 'wrap',
                    minHeight: '30px',
                  }),
                }}
              >
                {titleBarContent}
              </Box>
            )}
          </Box>
        </Paper>
      )}
      {topBar && topBar}
      <Box
        sx={[
          {
            px: 3,
            pb: 3,
            pt: 3,
            minWidth: 0,
            minHeight: 0,
            overflowX: 'auto',
          },
          ...(Array.isArray(contentSx) ? contentSx : contentSx ? [contentSx] : []),
        ]}
      >
        {children}
      </Box>
    </Box>
  );
};

PageLayout.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node.isRequired,
  contentSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  topBar: PropTypes.node,
  titleBarContent: PropTypes.node,
  afterBackContent: PropTypes.node,
  allowTitleBarWrap: PropTypes.bool,
  backTo: PropTypes.string,
  backLabel: PropTypes.string,
};

export default PageLayout;
